import { Commit } from 'vuex'

import Payment from '@/models/payment'

import axios from '@/axios-module'
import { CreditNotesReponse } from '@/schemas/creditNote'

import { CreditNoteMutationTypes } from './creditNotes'

export enum PaymentActionTypes {
  LoadByBookingId = '[Payments] LoadByBookingId',
  Get = '[Payments] Get',
}

export enum PaymentMutationTypes {
  LoadByBookingIdSuccess = '[Payments] LoadByBookingIdSuccess',
  LoadByBookingIdError = '[Payments] LoadByBookingIdError',
  GetSuccess = '[Payment] GetSuccess',
  GetFailure = '[Payment] GetFailure',
  SetLoaded = '[Payments] SetLoaded',
}

export interface State {
  paymentEntities: {
    [id: number]: Payment
  }
  paymentIds: number[]
  isLoaded: boolean
}

const initialState: State = {
  paymentEntities: {},
  paymentIds: [],
  isLoaded: false,
}

export default {
  state: initialState,
  getters: {
    getPaymentsByIds(state: State): (ids: number[]) => Payment[] {
      return (ids: number[]) =>
        (ids || []).map((id) => state.paymentEntities[id])
    },
    getPaymentById(state: State): (id: number) => Payment {
      return (id: number) => state.paymentEntities[id]
    },
  },
  actions: {
    async [PaymentActionTypes.LoadByBookingId](
      { commit }: { commit: Commit },
      bookingId: number,
    ) {
      commit(PaymentMutationTypes.SetLoaded, false)
      try {
        const response = await axios.get(
          `customer/bookings/${bookingId}/payments`,
        )
        commit(PaymentMutationTypes.LoadByBookingIdSuccess, {
          bookingId,
          payments: response.data,
        })
        commit(PaymentMutationTypes.SetLoaded, true)
      } catch {
        commit(PaymentMutationTypes.LoadByBookingIdError)
      }
    },
    async [PaymentActionTypes.Get](
      { commit }: { commit: Commit },
      { bookingId, paymentId }: { bookingId: number; paymentId: number },
    ) {
      try {
        const response = await axios.get(
          `/customer/bookings/${bookingId}/payments/${paymentId}`,
        )
        commit(PaymentMutationTypes.GetSuccess, response.data.payment)
      } catch {
        return commit(PaymentMutationTypes.GetFailure)
      }
    },
  },
  mutations: {
    [PaymentMutationTypes.LoadByBookingIdSuccess](
      state: State,
      payload: { payments: Payment[] },
    ) {
      const paymentEntities = payload.payments.reduce(
        (carry: { [id: number]: Payment }, payment: Payment) => {
          carry[payment.id] = payment
          return carry
        },
        {},
      )

      state.paymentEntities = {
        ...state.paymentEntities,
        ...paymentEntities,
      }
    },
    [PaymentMutationTypes.GetSuccess](state: State, payment: Payment) {
      state.paymentEntities = {
        ...state.paymentEntities,
        [payment.id]: payment,
      }

      if (!state.paymentIds.indexOf(payment.id)) {
        state.paymentIds = [...state.paymentIds, payment.id]
      }
    },
    [CreditNoteMutationTypes.LoadByBookingIdSuccess](
      state: State,
      creditNotesResponse: CreditNotesReponse,
    ) {
      state.paymentEntities = {
        ...state.paymentEntities,
        ...creditNotesResponse.entities.payments,
      }

      state.paymentIds = Object.keys(state.paymentEntities).map(Number)
    },
    [PaymentMutationTypes.SetLoaded](state: State, isLoaded: boolean) {
      state.isLoaded = isLoaded
    },
    [CreditNoteMutationTypes.LoadByBookingIdSuccess](
      state: State,
      creditNotesResponse: CreditNotesReponse,
    ) {
      state.paymentEntities = {
        ...state.paymentEntities,
        ...creditNotesResponse.entities.payments,
      }

      state.paymentIds = Object.keys(state.paymentEntities).map(Number)
    },
  },
}
